import Dexie from 'dexie';
import type { Table } from 'dexie';
import { db, READ_INDEX, SCHEMA_VERSION } from './db';
import { сПовторомПриОбрыве } from './dbReconnect';
import { PROFILE_LIMIT } from './looks';
import { wipeDevice } from './wipe';
import type { Animal, Color } from './looks';
import type { AttemptRow, DeviceRow, ProfileRow, RevealRow, SessionRow, StateRow } from './db';
import type { Hlc } from '../engine/hlc';
import type { FactId } from '../engine/facts';
import type { AttemptView } from '../engine/mastery';

export interface SessionPayload {
  session: SessionRow;
  attempts: AttemptRow[];
  reveals: RevealRow[];
}

const DEVICE_ID = 'singleton';

/** Раньше этой границы настоящих занятий не бывает: приложение выложено в
 *  2026 году. Отметка ниже означает планшет со сброшенными часами. */
const EPOCH_FLOOR = Date.UTC(2020, 0, 1);

/** Запас вперёд. Сутки, а не минуты, и это не небрежность.
 *
 *  Проверка ловит отметку, далеко разошедшуюся с собственными часами
 *  устройства, — сброшенный в 1970 год RTC, строку из чужого будущего при
 *  докачке, — но НЕ планшет, у которого неверна сама системная дата: там
 *  и отметка, и часы вызывающего приходят из одного и того же 2030 года, и
 *  локально отличить это не от чего. Против равномерно сбитых часов
 *  помогает только сверка с сервером (раздел 6 плана называет это
 *  сознательной потерей), и место для неё — приём строк на сервере в
 *  фазе 3. При пятиминутном запасе
 *  достаточно было часам уехать назад посреди тренировки (поправка по NTP,
 *  ребёнок покрутил дату в настройках), чтобы законная отметка ответа
 *  оказалась «в будущем» относительно момента записи, и всё занятие
 *  отвергалось целиком. Потерянная тренировка — цена выше, чем сутки
 *  неточности в отбраковке заведомо невозможных отметок. */
const FUTURE_TOLERANCE_MS = 24 * 60 * 60_000;

/** Проверки на входе в журнал.
 *
 *  Нарушение — исключение, а не пропуск строки. Молчаливый пропуск здесь
 *  хуже отказа: строка без профиля исчезает и из расчёта освоенности, и из
 *  отправки, а `NaN` в замере портит средние — и после синхронизации порча
 *  уезжает на все устройства сразу, где отличить её от настоящих данных
 *  уже нечем. Отказ записи виден: экран говорит «не удалось сохранить», а
 *  событие уходит в Sentry.
 *
 *  Отметки времени проверяются на обеих границах. Планшет со сброшенными
 *  часами вставляет попытки не туда, куда следовало бы, и освоенность по
 *  затронутым фактам считается неверно — это единственное смягчение потери
 *  причинности между устройствами, и без него оно остаётся словами. */
/** Верхняя граница отметки. Отдельной функцией, потому что без проверки
 *  самих часов вызывающего `NaN` в них молча выключал бы границу: любое
 *  сравнение с `NaN` ложно, и проверка «в будущем» превращалась бы в
 *  отсутствие проверки. */
function ceilingOf(nowMs: number): number {
  if (!Number.isFinite(nowMs)) throw new Error('часы вызывающего не число');
  return nowMs + FUTURE_TOLERANCE_MS;
}

/** Строка без часов или с `NaN` в них не попадает НИ В ОДИН из двух
 *  составных индексов: IndexedDB не индексирует запись, у которой нет
 *  компонента ключа, а `NaN` вообще не ключ. Такая строка видна в
 *  `count()`, но невидима и для чтения журнала, и для отправки — то есть не
 *  участвует в освоенности и не уезжает на сервер. Молча. */
function assertSequenced(row: { id: string; seq?: Hlc }, where: string): void {
  if (!row.seq) throw new Error(`${where}: нет логических часов`);
  if (!Number.isFinite(row.seq.ms) || !Number.isFinite(row.seq.counter)) {
    throw new Error(`${where}: логические часы не число`);
  }
}

/** Идентификатор обязан быть непустой строкой.
 *
 *  Типы этого не гарантируют: в базу приходят строки из файла выгрузки и —
 *  после фазы 3 — с сервера. А порядок IndexedDB ставит числовые ключи
 *  раньше строковых, тогда как `compareSeq` сравнил бы `20` и `'10'`
 *  приведением к числу: индекс и компаратор дали бы разный порядок на
 *  одинаковых часах, то есть разные ответы на вопрос «освоен ли пример». */
function assertId(id: unknown, where: string): void {
  if (typeof id !== 'string' || id === '') throw new Error(`${where}: идентификатор не строка`);
}

/** Корневой `deviceId` — первый компонент индекса отправки. Строка без него
 *  видна чтению журнала и невидима отправке: интерфейс покажет попытку,
 *  синхронизация не увидит её никогда. */
function assertDevice(deviceId: unknown, where: string): void {
  if (typeof deviceId !== 'string' || deviceId === '') {
    throw new Error(`${where}: нет устройства`);
  }
}

function assertStamp(value: number, where: string, ceiling: number): void {
  if (!Number.isFinite(value)) throw new Error(`${where}: отметка времени не число`);
  if (value < EPOCH_FLOOR) throw new Error(`${where}: отметка времени раньше 2020 года`);
  if (value > ceiling) throw new Error(`${where}: отметка времени в будущем`);
}

export function assertWritable(payload: SessionPayload, nowMs: number): void {
  const { profileId } = payload.session;
  if (!profileId) throw new Error('сессия без профиля');

  const ceiling = ceilingOf(nowMs);
  const stamp = (value: number, where: string): void => assertStamp(value, where, ceiling);
  const owned = (rowProfileId: string, where: string): void => {
    if (!rowProfileId) throw new Error(`${where}: строка без профиля`);
    if (rowProfileId !== profileId) throw new Error(`${where}: строка чужого профиля`);
  };
  /** Один и тот же `id` дважды в одной записи. Строки ложатся `put`-ом, то
   *  есть вторая молча заменила бы первую: двадцать заданий превратились бы
   *  в девятнадцать, а ошибка исчезла бы из окна «последние пять». */
  const seen = new Set<string>();
  const unique = (id: string, where: string): void => {
    if (seen.has(id)) throw new Error(`${where}: идентификатор повторяется в записи`);
    seen.add(id);
  };

  const session = payload.session;
  assertId(session.id, 'сессия');
  assertDevice(session.deviceId, `сессия ${session.id}`);
  stamp(session.startedAt, `сессия ${session.id} (начало)`);
  stamp(session.finishedAt, `сессия ${session.id} (конец)`);
  if (session.finishedAt < session.startedAt) {
    throw new Error(`сессия ${session.id}: закончилась раньше, чем началась`);
  }
  assertSequenced(session, `сессия ${session.id}`);

  for (const row of payload.attempts) {
    assertId(row.id, 'попытка');
    unique(row.id, `попытка ${row.id}`);
    owned(row.profileId, `попытка ${row.id}`);
    assertDevice(row.deviceId, `попытка ${row.id}`);
    assertSequenced(row, `попытка ${row.id}`);
    stamp(row.presentedAt, `попытка ${row.id}`);
    if (row.sessionId !== session.id) {
      throw new Error(`попытка ${row.id}: чужая сессия`);
    }
    if (!Number.isFinite(row.answerMs)) throw new Error(`попытка ${row.id}: замер не число`);
    if (row.answerMs < 0) throw new Error(`попытка ${row.id}: замер отрицателен`);
  }
  for (const row of payload.reveals) {
    assertId(row.id, 'раскрытие');
    unique(row.id, `раскрытие ${row.id}`);
    owned(row.profileId, `раскрытие ${row.id}`);
    assertDevice(row.deviceId, `раскрытие ${row.id}`);
    assertSequenced(row, `раскрытие ${row.id}`);
    stamp(row.revealedAt, `раскрытие ${row.id}`);
    if (row.sessionId !== session.id) {
      throw new Error(`раскрытие ${row.id}: чужая сессия`);
    }
  }
}

/** Границы диапазона одного профиля в составном индексе.
 *
 *  Массивные ключи сравниваются поэлементно, а более короткий массив с тем
 *  же началом идёт раньше длинного, — поэтому `[profileId]` ниже любой
 *  строки профиля. Верхняя граница — `[profileId, maxKey]`: `maxKey` это
 *  `[[]]`, то есть массив, а массивы в порядке ключей IndexedDB идут после
 *  чисел и строк, и остальные компоненты уже ни на что не влияют.
 *
 *  Форма именно префиксная, а не «minKey в каждый компонент»: последнюю
 *  `fake-indexeddb` отвергает как невалидный ключ (`DataError`), и тесты
 *  краснели бы на всех чтениях журнала. Заодно она не зависит от числа
 *  компонентов индекса. */
const lower = (profileId: string) => [profileId];
const upper = (profileId: string) => [profileId, Dexie.maxKey];

/** Одна транзакция и `put` по заранее присвоенным `id`. Повтор после сбоя
 *  не создаёт дублей: Dexie документирует нестабильность IndexedDB в Safari
 *  при пробуждении вкладки, то есть возможен коммит без события
 *  завершения (раздел 4.5).
 *
 *  Та же нестабильность бьёт и просто, без пробуждения: Safari рвёт
 *  соединение, пока вкладка в фоне, и следующая транзакция падает
 *  `UnknownError`/`InvalidStateError`, а Dexie сама это не лечит (раздел
 *  «Синхронизация», iPhone). `сПовторомПриОбрыве` закрывает и заново
 *  открывает базу и повторяет ВСЮ транзакцию один раз — она атомарна, и
 *  повтор по тем же `id` не создаёт дублей, ровно как и повтор после
 *  пропавшего события завершения выше. */
export async function saveSession(payload: SessionPayload, nowMs = Date.now()): Promise<void> {
  assertWritable(payload, nowMs);
  const tables = [db.sessions, db.attempts, db.reveals, db.profiles, db.device];
  await сПовторомПриОбрыве(() => db.transaction('rw', tables, async () => {
    /** Профиль обязан существовать, а не просто совпадать со своими же
     *  строками. Сверка строк друг с другом не ловит призрака: устаревшее
     *  замыкание или гонка со сменой профиля отдают согласованный payload с
     *  идентификатором, которого в `profiles` нет, — и занятие ложится в
     *  базу под несуществующего ребёнка. На экранах его нет, потому что
     *  читают они активный профиль, а чистить нечего: строки не его. */
    const profile = await db.profiles.get(payload.session.profileId);
    if (!profile) {
      throw new Error(`сессия ${payload.session.id}: профиля не существует`);
    }
    if (profile.deletedAt !== null) {
      throw new Error(`сессия ${payload.session.id}: профиль удалён`);
    }
    /** Существования профиля мало — он обязан быть АКТИВНЫМ в момент
     *  записи, и читается это в той же транзакции.
     *
     *  Сверка строк друг с другом ловит призрака, но не подмену: вторая
     *  вкладка переключила ребёнка, пока в первой шла тренировка, и
     *  согласованное занятие ложится под прежнего — на экранах его нет,
     *  освоенность чужого ребёнка растёт молча. Из двух зол выбран громкий
     *  отказ: экран скажет «не удалось сохранить», событие уйдёт в Sentry,
     *  и родитель потеряет одну тренировку. Тихая порча освоенности не
     *  лечится вовсе, потому что отличить испорченное от настоящего
     *  задним числом нечем. */
    const device = await db.device.get(DEVICE_ID);
    if (device?.activeProfileId !== payload.session.profileId) {
      throw new Error(`сессия ${payload.session.id}: профиль сменился во время тренировки`);
    }
    await db.sessions.put(payload.session);
    if (payload.attempts.length > 0) await db.attempts.bulkPut(payload.attempts);
    if (payload.reveals.length > 0) await db.reveals.bulkPut(payload.reveals);
  }));
}

/** Журнал попыток одного профиля, в порядке `compareSeq`.
 *
 *  Порядок берётся из составного индекса `[profileId+seq.ms+seq.counter+id]`,
 *  а не сортировкой в памяти: массивные ключи IndexedDB сравниваются
 *  поэлементно, то есть индекс даёт ровно тот порядок, что компаратор.
 *
 *  Чего этот индекс НЕ делает: он не сокращает объём чтения. Освоенность
 *  считает окна по всему журналу своего профиля, и все его строки
 *  по-прежнему читаются. Настоящее сокращение — окно или пагинация, и их
 *  здесь нет. */
export async function loadAttemptViews(profileId: string): Promise<AttemptView[]> {
  const rows = await db.attempts
    .where(READ_INDEX)
    .between(lower(profileId), upper(profileId))
    .toArray();
  return rows.map((row, index) => ({
    factIds: row.factIds,
    exerciseType: row.exerciseType,
    isCorrect: row.isCorrect,
    answerMs: row.answerMs,
    resumedAfterBackground: row.resumedAfterBackground,
    // Стенное время, а не физическая часть логических часов: освоенность
    // меряет по atMs ДЛИТЕЛЬНОСТИ (12 часов разброса, 12 часов после
    // раскрытия), а логические часы при слиянии продвигаются искусственно,
    // и разности перестают быть настоящими интервалами.
    atMs: row.presentedAt,
    order: index,
  }));
}

export async function loadRevealViews(
  profileId: string,
): Promise<{ factId: FactId; atMs: number }[]> {
  const rows = await db.reveals
    .where(READ_INDEX)
    .between(lower(profileId), upper(profileId))
    .toArray();
  return rows.map((row) => ({ factId: row.factId, atMs: row.revealedAt }));
}

/** Курсор раздатчика хранится здесь, а не в `localStorage`: без него за
 *  39 сессий 40,6% фактов не набрали бы трёх зачётных появлений
 *  (раздел 5.1). Строка на профиль: двое детей на одном планшете обязаны
 *  раздавать независимо. */
export async function loadState(profileId: string): Promise<StateRow | undefined> {
  return db.state.get(profileId);
}

export async function saveState(profileId: string, state: Omit<StateRow, 'id'>): Promise<void> {
  await db.state.put({ ...state, id: profileId });
}

export async function loadDevice(): Promise<DeviceRow | undefined> {
  return db.device.get(DEVICE_ID);
}

export async function saveDevice(device: Omit<DeviceRow, 'id'>): Promise<void> {
  await db.device.put({ ...device, id: DEVICE_ID });
}

export interface Look {
  color: Color;
  animal: Animal;
}

/** Живой профиль — тот, у которого нет отметки надгробия. Отдельной
 *  функцией, а не сравнением по месту: условие «живой» встречается в семи
 *  местах, и забытое в одном из них означает удалённого ребёнка на экране
 *  выбора либо занятия, дописанные в надгробие. */
const alive = (profile: ProfileRow): boolean => profile.deletedAt === null;

/** Профили для экранов: живые, в порядке появления. */
export async function loadProfiles(): Promise<ProfileRow[]> {
  return (await db.profiles.orderBy('createdAt').toArray()).filter(alive);
}

/** Все профили, включая надгробия. Нужен ровно одному потребителю —
 *  отправке в фазе 3: сервер обязан узнать об удалении, а узнать он может
 *  только из надгробия. Экранам этот список показывать нельзя. */
export async function loadAllProfiles(): Promise<ProfileRow[]> {
  return db.profiles.orderBy('createdAt').toArray();
}

/** Строка устройства, заводя её при необходимости.
 *
 *  Отделена от профилей намеренно. Идентификатор устройства нужен раньше
 *  первого профиля: он стоит первым компонентом индекса отправки, и строка
 *  журнала без него невидима для синхронизации. Профиль же теперь заводит
 *  родитель на экране создания, а не приложение само.
 *
 *  Одной транзакцией: StrictMode монтирует эффекты дважды, и два
 *  одновременных запуска иначе завели бы устройству два разных
 *  идентификатора. */
export async function ensureDevice(makeId: () => string): Promise<DeviceRow> {
  return db.transaction('rw', db.device, async () => {
    const existing = await db.device.get(DEVICE_ID);
    if (existing) return existing;
    const device: DeviceRow = {
      id: DEVICE_ID,
      deviceId: makeId(),
      storagePersisted: null,
      activeProfileId: null,
      schemaVersion: SCHEMA_VERSION,
      ownerId: null,
    };
    await db.device.put(device);
    return device;
  });
}

/** Чем кончилась попытка закрепить устройство за вошедшим родителем. */
export type DeviceClaim =
  /** На устройстве не было владельца: либо это первый вход, либо занятия
   *  шли до входа. Данные достаются вошедшему.
   *
   *  `profiles` — сколько живых детей на устройстве, и спрашивать надо
   *  при любом их числе больше нуля.
   *
   *  Здесь был соблазн, и он оказался дырой. Хотелось не пугать давние
   *  установки, где дети и так в облаке, и считать только тех, кто ещё ни
   *  разу не был ни на одном сервере (`serverKnown === false`). Но
   *  `serverKnown` говорит «эта строка есть на КАКОМ-ТО сервере», а не «на
   *  сервере ЭТОГО родителя». У давней установки счёт выходил нулевым, и
   *  устройство закреплялось за вошедшим МОЛЧА — то есть ровно тот дефект,
   *  ради которого всё это писалось: сессия А умерла, вошёл Б, и дети
   *  семьи А оказались внутри здорового с виду сеанса Б.
   *
   *  `fresh` — сколько из них впервые обретает владельца. Число нужно
   *  только для выбора слов на экране, но не для решения спрашивать.
   *  Больше нуля — занятия, шедшие без записи, только что стали
   *  принадлежать конкретной почте, и об этом надо СКАЗАТЬ: иначе взрослый,
   *  вошедший не в ту запись (свою прежнюю, супруга, чужую), узнает об этом
   *  только когда занятия не найдутся там, где он их ищет. */
  | { kind: 'adopted'; profiles: number; fresh: number }
  /** Тот же родитель, что и был. Обычный случай, ничего не происходит. */
  | { kind: 'same' }
  /** Вошёл ДРУГОЙ родитель, а на устройстве остались занятия прежнего.
   *  Решение — за взрослым у экрана, и до этого решения обмен не начинается:
   *  чужие строки не должны уехать под чужой учётной записью. */
  | { kind: 'foreign'; profiles: number };

/** Сверка устройства с вошедшим родителем — ПЕРВОЕ, что делается после входа.
 *
 *  Порядок несущий. Пока владелец не сверен, нельзя ни отправлять, ни
 *  докачивать: отправка унесла бы занятия прежней семьи под записью новой
 *  (сервер их отвергнет, но экран покажет отказ вместо объяснения), а
 *  докачка подмешала бы к детям прежней семьи детей новой — и взрослый
 *  увидел бы на одном экране чужого ребёнка.
 *
 *  Функция ничего не стирает сама. Стирание необратимо, и решать его должен
 *  взрослый, которому назвали цену, а не транзакция. */
export async function claimDevice(parentId: string): Promise<DeviceClaim> {
  return db.transaction('rw', db.device, db.profiles, async () => {
    const device = await db.device.get(DEVICE_ID);
    /** Строки устройства ещё нет — значит нет и данных. Заводить её здесь
     *  не наше дело: этим занимается `ensureDevice`, и он же выдаёт
     *  идентификатор. Ответ «усыновлено» тут честен: усыновлять нечего. */
    if (!device) return { kind: 'adopted', profiles: 0, fresh: 0 } as const;
    if (device.ownerId === parentId) return { kind: 'same' } as const;
    const living = (await db.profiles.toArray()).filter(alive);
    /** Отметку здесь НЕ ставим, и это главное свойство этой функции.
     *
     *  Ставили — и получалась ловушка ровно из того текста, который сами же
     *  показываем: экран усыновления советует «если это не та почта,
     *  выйдите и войдите в нужную», а отметка к этому моменту уже стояла на
     *  неверной записи. Вход в правильную давал «чужое устройство», и
     *  единственной кнопкой оставалось «стереть». То есть совет на экране
     *  уничтожал доступ к занятиям.
     *
     *  Теперь сверка только читает. Пишет `adoptDevice`, и только после
     *  того, как взрослый сказал «да». */
    if (device.ownerId === null) {
      return {
        kind: 'adopted',
        profiles: living.length,
        fresh: living.filter((row) => !row.serverKnown).length,
      } as const;
    }
    return { kind: 'foreign', profiles: living.length } as const;
  });
}

/** Закрепление устройства за вошедшим — БЕЗ стирания.
 *
 *  Отдельно от сверки, потому что между ними стоит человек: сверка
 *  сообщает, что данные ничьи, взрослый видит, в какую почту они уедут, и
 *  только тогда отметка ставится. Незакреплённое устройство переживает
 *  закрытие вкладки и спросит снова — это верно и безопасно. */
export async function adoptDevice(parentId: string): Promise<void> {
  await db.transaction('rw', db.device, async () => {
    const device = await db.device.get(DEVICE_ID);
    /** Молчаливого «ничего не сделали» тут быть не должно.
     *
     *  Заслон открывает доступ к занятиям, считая, что владелец записан.
     *  Если запись не состоялась — строки нет, владельца перебили из другой
     *  вкладки, — то следующий вошедший снова окажется усыновителем, и
     *  защиты не будет ни от чего. Это нарушенное предусловие, а по правилам
     *  проекта такое падает громко, а не продолжается тихо. */
    if (!device) throw new Error('закрепление устройства без строки устройства');
    if (device.ownerId === parentId) return;
    if (device.ownerId !== null) {
      throw new Error('закрепление устройства, у которого уже другой владелец');
    }
    await db.device.put({ ...device, ownerId: parentId });
  });
}

/** Стирание чужих данных и закрепление устройства за вошедшим.
 *
 *  Отдельной функцией от `claimDevice`, потому что между ними стоит человек:
 *  `claimDevice` сообщает, что данные чужие, взрослый решает, а эта функция
 *  исполняет решение. Слить их в одну значило бы стирать занятия молча. */
export async function takeOverDevice(parentId: string): Promise<void> {
  await wipeDevice();
  await db.transaction('rw', db.device, async () => {
    const device = await db.device.get(DEVICE_ID);
    /** Стирание уже произошло. Уйти отсюда, не поставив владельца, значит
     *  оставить наполовину сделанное разрушительное действие: данных нет, а
     *  устройство снова ничьё. Падаем громко — это видно и чинится входом
     *  заново, в отличие от тихого расхождения. */
    if (!device) throw new Error('передача устройства без строки устройства');
    await db.device.put({ ...device, ownerId: parentId });
  });
}

/** Дошло ли надгробие профиля до сервера.
 *
 *  Нужно ровно для одного: не говорить «удалён навсегда», пока это неправда.
 *  `sync()` возвращается и тогда, когда обмен НЕ пошёл — идёт предыдущий,
 *  идёт тренировка, мёртвый токен, нет учётной записи, — и «дождались
 *  обмена» не означает «сервер узнал». Спрашиваем у самой строки.
 *
 *  Отсутствие строки — тоже «дошло»: её сносит авторитетный список с
 *  сервера, а он приходит только после того, как надгробие принято. */
export async function tombstoneDelivered(profileId: string): Promise<boolean> {
  const row = await db.profiles.get(profileId);
  return !row || !row.needsPush;
}

/** Активный профиль устройства, или `null`, если живых профилей нет вовсе.
 *
 *  Транзакция на запись, потому что функция ещё и чинит висячую ссылку:
 *  `activeProfileId` может указывать на удалённый профиль (удалили в другой
 *  вкладке) или на несуществующий (файл базы перенесли). Оставить ссылку
 *  висячей значит показывать экран создания при живых профилях либо, хуже,
 *  писать занятия в надгробие. */
export async function loadActiveProfile(): Promise<ProfileRow | null> {
  return db.transaction('rw', [db.profiles, db.device], async () => {
    const device = await db.device.get(DEVICE_ID);
    const known = device?.activeProfileId
      ? await db.profiles.get(device.activeProfileId)
      : undefined;
    if (known && alive(known)) return known;

    const fallback = (await db.profiles.orderBy('createdAt').toArray()).find(alive) ?? null;
    if (device && device.activeProfileId !== (fallback?.id ?? null)) {
      await db.device.put({ ...device, activeProfileId: fallback?.id ?? null });
    }
    return fallback;
  });
}

/** Переключение ребёнка. Отказ, а не тихая правка: переключиться на
 *  удалённый профиль нельзя, и делать вид, что получилось, — значит
 *  показать пустые занятия вместо объяснения. */
export async function setActiveProfile(profileId: string): Promise<void> {
  await db.transaction('rw', [db.profiles, db.device], async () => {
    const profile = await db.profiles.get(profileId);
    if (!profile) throw new Error(`профиль ${profileId} не существует`);
    if (!alive(profile)) throw new Error(`профиль ${profileId} удалён`);
    const device = await db.device.get(DEVICE_ID);
    if (!device) throw new Error('строки устройства нет');
    await db.device.put({ ...device, activeProfileId: profileId });
  });
}

/** Заведение профиля.
 *
 *  Два предела проверяются здесь, а не в интерфейсе, и это не
 *  перестраховка. Интерфейс гасит занятые сочетания и прячет кнопку на
 *  двенадцатом профиле, но между отрисовкой экрана и нажатием помещается
 *  вторая вкладка, а после фазы 3 — ещё и другое устройство. Предел числа
 *  профилей исполняет и сервер (`profiles_limit`), уникальность пары — тоже
 *  (частичный индекс среди живых); здесь тот же ответ дают до сети, чтобы
 *  родитель увидел отказ сразу, а не после «сохраняем». */
export async function createProfile(
  look: Look,
  makeId: () => string,
  nowMs: number,
): Promise<ProfileRow> {
  return db.transaction('rw', [db.profiles, db.device], async () => {
    const living = (await db.profiles.toArray()).filter(alive);
    if (living.length >= PROFILE_LIMIT) {
      throw new Error(`больше ${PROFILE_LIMIT} профилей нельзя`);
    }
    if (living.some((p) => p.color === look.color && p.animal === look.animal)) {
      throw new Error('такой цвет и зверёк уже заняты');
    }
    const profile: ProfileRow = {
      id: makeId(),
      color: look.color,
      animal: look.animal,
      createdAt: nowMs,
      serverKnown: false,
      deletedAt: null,
      needsPush: true,
      schemaVersion: SCHEMA_VERSION,
    };
    await db.profiles.add(profile);

    /** Первый заведённый профиль становится активным сам: иначе экран
     *  создания вернул бы родителя на выбор из одного, которого некому
     *  выбрать. */
    const device = await db.device.get(DEVICE_ID);
    if (!device) throw new Error('строки устройства нет');
    if (device.activeProfileId === null) {
      await db.device.put({ ...device, activeProfileId: profile.id });
    }
    return profile;
  });
}

/** Смена облика. Имени у профиля нет, менять больше нечего. */
export async function updateProfileLook(profileId: string, look: Look): Promise<void> {
  await db.transaction('rw', db.profiles, async () => {
    const profile = await db.profiles.get(profileId);
    if (!profile) throw new Error(`профиль ${profileId} не существует`);
    if (!alive(profile)) throw new Error(`профиль ${profileId} удалён`);
    const taken = (await db.profiles.toArray()).some(
      (p) => p.id !== profileId && alive(p) && p.color === look.color && p.animal === look.animal,
    );
    if (taken) throw new Error('такой цвет и зверёк уже заняты');
    await db.profiles.put({ ...profile, color: look.color, animal: look.animal, needsPush: true });
  });
}

/** Удаление профиля: одно действие, «удалить навсегда».
 *
 *  Занятия сносятся, строка профиля остаётся надгробием. Почему не удалить
 *  и её — в комментарии к `ProfileRow.deletedAt`.
 *
 *  Журналы выбираются перебором по `profileId`, а НЕ диапазоном по индексу
 *  чтения, и это осознанно дороже. Диапазон видит только строки, у которых
 *  есть все компоненты составного ключа: строка без `seq` в него не входит
 *  и пережила бы «удалить навсегда», оставшись в базе после того, как
 *  родителю сказали, что занятий больше нет. Сторож на входе такие строки
 *  не пропускает, но удаление — не то место, где стоит опираться на
 *  «не пропускает»: перебор по двенадцати профилям случается раз в жизни
 *  профиля, а незамеченный остаток — это чужие данные на устройстве. */
export async function deleteProfile(profileId: string, nowMs = Date.now()): Promise<void> {
  /** Карантин здесь наравне с журналами, и его пропуск был бы не мелочью.
   *
   *  В строке карантина лежит `payload` — целая серверная строка журнала
   *  ЭТОГО ребёнка. Политика обещает, что удаление профиля стирает занятия
   *  с устройства сразу и необратимо; оставить карантин значит сделать это
   *  обещание неправдой, причём незаметно: на экранах ребёнка нет, а его
   *  занятия лежат в базе браузера.
   *
   *  Ошибку легко повторить: то же самое стирание есть в трёх местах —
   *  здесь, в `wipeProfileLocally` (снос по решению сервера) и в
   *  `wipeDevice` (выход). Пропустить таблицу в одном из трёх и не заметить
   *  проще всего. */
  const tables = [
    db.profiles, db.device, db.attempts, db.reveals, db.sessions, db.state, db.syncState,
    db.notSent, db.quarantine,
  ];
  await db.transaction('rw', tables, async () => {
    const profile = await db.profiles.get(profileId);
    if (!profile) throw new Error(`профиль ${profileId} не существует`);
    /** Повторное удаление — не ошибка. Две вкладки, два нажатия, повтор
     *  после сбоя: отметка уже стоит, занятий уже нет, делать нечего.
     *  Перезаписать `deletedAt` вторым временем значило бы двигать
     *  надгробие. */
    if (!alive(profile)) return;

    const own = (row: { profileId: string }) => row.profileId === profileId;
    await db.attempts.filter(own).delete();
    await db.reveals.filter(own).delete();
    await db.sessions.filter(own).delete();
    await db.state.delete(profileId);
    await db.syncState.where('[profileId+table]').between([profileId], [profileId, Dexie.maxKey]).delete();
    /** След непринятых уходит вместе с ребёнком: строк, о которых он
     *  говорит, больше нет. */
    await db.notSent.where('profileId').equals(profileId).delete();
    await db.quarantine.where('profileId').equals(profileId).delete();
    await db.profiles.put({ ...profile, deletedAt: nowMs, needsPush: true });

    const device = await db.device.get(DEVICE_ID);
    if (device?.activeProfileId === profileId) {
      const next = (await db.profiles.toArray()).sort((a, b) => a.createdAt - b.createdAt).find(alive);
      await db.device.put({ ...device, activeProfileId: next?.id ?? null });
    }
  });
}

/** Нижняя граница логических часов профиля на этом устройстве.
 *
 *  Нужна ровно для одного случая, и случай этот уже описан в списке
 *  незакрытого: строка `state` пропала, а журнал уцелел. Хранилище может
 *  вытеснить их по-разному, и обновление тоже.
 *
 *  Без границы часы начинаются заново от текущего времени. Если стенные
 *  часы устройства к тому моменту отстали — а они отстают: сброшенный RTC,
 *  ручная правка даты, — новые записи получают отметки НИЖЕ курсора
 *  отправки. Диапазон отправки идёт «строго выше курсора», и такие записи
 *  не уедут на сервер никогда. Молча: на устройстве они есть, на других нет,
 *  признака «не отправлено» в строке не существует.
 *
 *  Граница берётся с двух сторон: из курсора отправки (то, что мы обещали
 *  превзойти) и из последней собственной строки журнала (то, что уже
 *  записано). Возвращается `null`, если ни того, ни другого нет — тогда
 *  начинать заново безопасно. */
export async function seqFloor(profileId: string): Promise<Hlc | null> {
  const cursors = await db.syncState
    .where('[profileId+table]')
    .between([profileId], [profileId, Dexie.maxKey])
    .toArray();

  let best: { ms: number; counter: number } | null = null;
  const consider = (candidate: { ms: number; counter: number } | undefined | null) => {
    if (!candidate || !Number.isFinite(candidate.ms) || !Number.isFinite(candidate.counter)) return;
    if (!best || candidate.ms > best.ms || (candidate.ms === best.ms && candidate.counter > best.counter)) {
      best = { ms: candidate.ms, counter: candidate.counter };
    }
  };
  for (const cursor of cursors) consider(cursor.pushedUpTo);

  const last = async <T extends { seq?: Hlc }>(table: Table<T, string>) =>
    table.where(READ_INDEX).between(lower(profileId), upper(profileId)).reverse().first();
  for (const table of [db.attempts, db.reveals, db.sessions]) {
    consider((await last(table as Table<{ seq?: Hlc }, string>))?.seq);
  }
  if (!best) return null;
  const found = best as { ms: number; counter: number };
  return { ms: found.ms, counter: found.counter, deviceId: '' };
}

/** Сессии для экрана истории, свежие первыми. Порядок берётся из
 *  объявленного индекса `[profileId+startedAt]`, а не сортировкой в памяти:
 *  таблица растёт на строку за тренировку и переживёт годы занятий.
 *
 *  Это стенное время, и оно единственное здесь уместно: история —
 *  человеческая шкала «когда я занимался», а не журнал для слияния.
 *
 *  Оба чтения ниже открывают курсор, а именно на открытии курсора WebKit
 *  роняет `UnknownError`, если Safari успел за спиной приложения оборвать
 *  соединение (раздел «Синхронизация», iPhone; Sentry MULT-TRAINER-9).
 *  `сПовторомПриОбрыве` — чистое чтение, повтор ничего не портит. */
export async function loadSessions(profileId: string, limit?: number): Promise<SessionRow[]> {
  return сПовторомПриОбрыве(() => loadSessionsOnce(profileId, limit));
}

async function loadSessionsOnce(profileId: string, limit?: number): Promise<SessionRow[]> {
  /** Сессии без единой попытки не показываются.
   *
   *  Решение спеки (раздел 4.6), и до появления облака оно выглядело
   *  придиркой: локально сессия и её попытки пишутся одной транзакцией.
   *  С докачкой это перестаёт быть верным — строка сессии и её попытки
   *  приезжают разными страницами и разными таблицами. Между двумя
   *  докачками история показывала бы «20 заданий, 0 ошибок, 95%» при пустом
   *  журнале: сводка в строке сессии есть, а подтвердить её нечем.
   *
   *  Различие берётся по ключам индекса `sessionId`, а не перебором
   *  журнала: `uniqueKeys` читает только сами ключи, и цена — число
   *  РАЗЛИЧНЫХ сессий, а не число попыток. За год занятий это разница
   *  между тремя сотнями и тридцатью тысячами. */
  const [ordered, withAttempts] = await Promise.all([
    db.sessions
      .where('[profileId+startedAt]')
      .between(lower(profileId), upper(profileId))
      .reverse()
      .toArray(),
    db.attempts.orderBy('sessionId').uniqueKeys(),
  ]);
  const answered = new Set(withAttempts as string[]);
  const shown = ordered.filter((row) => answered.has(row.id));
  return limit === undefined ? shown : shown.slice(0, limit);
}

/** Формат выгрузки профиля. Версия формата своя, отдельная от версии схемы:
 *  файл живёт на диске у родителя и может пережить несколько миграций базы. */
export const EXPORT_FORMAT = 'mult-trainer-profile';
export const EXPORT_FORMAT_VERSION = 1;

export interface ProfileExport {
  format: typeof EXPORT_FORMAT;
  formatVersion: number;
  exportedAt: number;
  profile: ProfileRow;
  sessions: SessionRow[];
  attempts: AttemptRow[];
  reveals: RevealRow[];
}

export interface ImportSummary {
  profileId: string;
  профильСоздан: boolean;
  sessions: number;
  attempts: number;
  reveals: number;
}

/** Выгрузка журнала одного профиля целиком.
 *
 *  Одной транзакцией: без неё между чтением попыток и чтением сессий
 *  помещается целая завершённая тренировка, и в файл попала бы сессия без
 *  своих попыток — та самая «20 заданий, 0 ошибок» при пустом журнале,
 *  которую спека уже признала недоставленной. */
export async function exportProfile(
  profileId: string,
  nowMs = Date.now(),
): Promise<ProfileExport> {
  return db.transaction('r', [db.profiles, db.attempts, db.reveals, db.sessions], async () => {
    const profile = await db.profiles.get(profileId);
    if (!profile) throw new Error(`профиль ${profileId} не существует`);
    const range = <T>(table: Table<T, string>) =>
      table.where(READ_INDEX).between(lower(profileId), upper(profileId)).toArray();
    return {
      format: EXPORT_FORMAT,
      formatVersion: EXPORT_FORMAT_VERSION,
      exportedAt: nowMs,
      profile,
      sessions: await range(db.sessions),
      attempts: await range(db.attempts),
      reveals: await range(db.reveals),
    };
  });
}

/** Разбор и проверка файла выгрузки.
 *
 *  Отдельно от применения, потому что отвергнуть надо ДО первой записи:
 *  наполовину загруженный журнал хуже незагруженного — в нём нельзя
 *  отличить пропущенное от того, чего не было. */
export function assertImportable(data: unknown, nowMs: number): ProfileExport {
  const file = data as Partial<ProfileExport> | null;
  if (!file || typeof file !== 'object') throw new Error('это не файл выгрузки');
  if (file.format !== EXPORT_FORMAT) throw new Error('это не файл выгрузки тренажёра');
  if (file.formatVersion !== EXPORT_FORMAT_VERSION) {
    throw new Error(`неизвестная версия формата: ${String(file.formatVersion)}`);
  }
  const profile = file.profile;
  if (!profile?.id || !profile.color || !profile.animal) {
    throw new Error('в файле нет профиля');
  }
  const { sessions = [], attempts = [], reveals = [] } = file;
  if (!Array.isArray(sessions) || !Array.isArray(attempts) || !Array.isArray(reveals)) {
    throw new Error('в файле испорчены журналы');
  }

  const ceiling = ceilingOf(nowMs);
  /** Загрузка — восстановление в ТОТ ЖЕ профиль, поэтому `id` строк
   *  сохраняются, и каждая строка обязана принадлежать профилю из файла.
   *  Клонирование в новый профиль потребовало бы перегенерации `id`
   *  (`attempts.id` уникален глобально), и журнал перестал бы быть тем же;
   *  а с сохранёнными `id` загрузка в чужой профиль после появления
   *  сервера молча упёрлась бы в занятые идентификаторы. */
  const own = (rowProfileId: string, where: string) => {
    if (rowProfileId !== profile.id) throw new Error(`${where}: строка чужого профиля`);
  };
  for (const row of sessions) {
    assertId(row.id, 'сессия');
    own(row.profileId, `сессия ${row.id}`);
    assertDevice(row.deviceId, `сессия ${row.id}`);
    assertSequenced(row, `сессия ${row.id}`);
    assertStamp(row.startedAt, `сессия ${row.id} (начало)`, ceiling);
    assertStamp(row.finishedAt, `сессия ${row.id} (конец)`, ceiling);
  }
  for (const row of attempts) {
    assertId(row.id, 'попытка');
    own(row.profileId, `попытка ${row.id}`);
    assertDevice(row.deviceId, `попытка ${row.id}`);
    assertSequenced(row, `попытка ${row.id}`);
    assertStamp(row.presentedAt, `попытка ${row.id}`, ceiling);
    if (!Number.isFinite(row.answerMs)) throw new Error(`попытка ${row.id}: замер не число`);
    if (row.answerMs < 0) throw new Error(`попытка ${row.id}: замер отрицателен`);
  }
  for (const row of reveals) {
    assertId(row.id, 'раскрытие');
    own(row.profileId, `раскрытие ${row.id}`);
    assertDevice(row.deviceId, `раскрытие ${row.id}`);
    assertSequenced(row, `раскрытие ${row.id}`);
    assertStamp(row.revealedAt, `раскрытие ${row.id}`, ceiling);
  }
  return { ...file, profile, sessions, attempts, reveals } as ProfileExport;
}

/** Загрузка выгруженного журнала обратно.
 *
 *  Это восстановление, а не клонирование: строки ложатся `put`-ом по своим
 *  `id`, поэтому повторная загрузка того же файла ничего не задваивает —
 *  ровно то же свойство, на котором держится слияние двух устройств.
 *
 *  Профиль создаётся, если его на устройстве нет: это и есть страховка от
 *  потерянного планшета, ради которой выгрузка вообще нужна. */
export async function importProfile(data: unknown, nowMs = Date.now()): Promise<ImportSummary> {
  const file = assertImportable(data, nowMs);
  return db.transaction(
    'rw',
    [db.profiles, db.attempts, db.reveals, db.sessions],
    async () => {
      const existing = await db.profiles.get(file.profile.id);
      /** Загрузка в надгробие запрещена. Родитель нажал «удалить
       *  навсегда», а файл вернул бы занятия того же ребёнка обратно —
       *  причём на сервер они уже не уедут: политика запрещает дописывать
       *  в удалённый профиль. Получился бы профиль, видимый на одном
       *  устройстве и ни на каком другом. */
      if (existing && !alive(existing)) {
        throw new Error('этот профиль удалён; загрузка в него невозможна');
      }
      if (!existing) {
        const living = (await db.profiles.toArray()).filter(alive);
        if (living.length >= PROFILE_LIMIT) {
          throw new Error(`больше ${PROFILE_LIMIT} профилей нельзя`);
        }
        /** Пара «цвет и зверёк» уникальна среди живых профилей устройства.
         *  Занята она может быть только другим ребёнком, и молча сливать их
         *  журналы нельзя — отказ внятным текстом, а выбор нового зверя за
         *  родителем. */
        const taken = living.some(
          (p) => p.color === file.profile.color && p.animal === file.profile.animal,
        );
        if (taken) {
          throw new Error('такой цвет и зверёк уже заняты другим профилем');
        }
        await db.profiles.add({
          ...file.profile, serverKnown: false, deletedAt: null, needsPush: true,
        });
      }
      if (file.sessions.length > 0) await db.sessions.bulkPut(file.sessions);
      if (file.attempts.length > 0) await db.attempts.bulkPut(file.attempts);
      if (file.reveals.length > 0) await db.reveals.bulkPut(file.reveals);
      return {
        profileId: file.profile.id,
        профильСоздан: !existing,
        sessions: file.sessions.length,
        attempts: file.attempts.length,
        reveals: file.reveals.length,
      };
    },
  );
}
